import {formatLength} from './units.js';

export const SPLIT_EVIDENCE_LIMITS=Object.freeze({nodes:100000,characters:2*1024*1024,depth:32});
/** Charge before the lineage assessor clones complete source snapshots. The
 * same budget is shared across all floors, including repeated stored copies. */
export function chargeWallSplitEvidence(values,budget){
  const path=new WeakSet();
  const fail=()=>{const error=new Error('Retained split originals exceed the bounded evidence review. Remaining whole-wall originals were not evaluated.');error.code='split-evidence-budget';throw error;};
  const visit=(value,depth)=>{
    if(++budget.nodes>SPLIT_EVIDENCE_LIMITS.nodes||depth>SPLIT_EVIDENCE_LIMITS.depth)fail();
    if(typeof value==='string'){budget.characters+=value.length;if(budget.characters>SPLIT_EVIDENCE_LIMITS.characters)fail();}
    if(!value||typeof value!=='object')return;
    if(path.has(value))throw new Error('A retained split original contains cyclic data.');
    path.add(value);
    for(const key in value)if(Object.hasOwn(value,key)){
      budget.characters+=key.length;if(budget.characters>SPLIT_EVIDENCE_LIMITS.characters)fail();
      visit(value[key],depth+1);
    }
    path.delete(value);
  };
  visit(values,0);
}

/** The assessor validates the retained source and floor-local pair. This
 * display snapshot does not label either child as an independently read span. */
export function wallSplitEvidence(assessment){
  const s=assessment,status=s.status==='missing-part'?'missing-reference':s.status==='different-floor'?'geometry-changed':s.status==='current'?'unverified':s.status;
  return {status,severity:status==='invalid'?'error':'warning',assumption:true,
    title:`${typeof s.sourceWall?.name==='string'?s.sourceWall.name.slice(0,120):'Wall'}: original whole-wall evidence`,
    detail:`${s.message} Retained readings, scan provenance and layout inputs describe the ORIGINAL WHOLE WALL, not two separately measured pieces. Splitting does not provide a new measurement or verify either piece.`,
    splitOriginal:{splitID:s.splitID,originalWallID:s.sourceWall.id,recordedFloorID:s.recordedFloorID,status:s.status,
      originalMetres:s.originalMetres,currentMetres:s.currentMetres,originalA:structuredClone(s.originalA),originalB:structuredClone(s.originalB),
      ...(s.currentA?{currentA:structuredClone(s.currentA)}:{}),...(s.currentB?{currentB:structuredClone(s.currentB)}:{})}};
}

/** Shared allowlisted rows; UI renders text and the report escapes HTML. */
export function wallSplitEvidenceRows(item,doc){
  const s=item.splitOriginal;if(!s)return[];
  const rows=[['Evidence scope','Original whole wall — not separately measured pieces'],['Original wall identity',s.originalWallID],['Original floor identity',s.recordedFloorID],['Split identity',s.splitID]];
  const coords=p=>`X ${formatLength(p.x,doc,3)} · Y ${formatLength(p.y,doc,3)}`;
  rows.push(['Original whole-wall A',coords(s.originalA)],['Original whole-wall B',coords(s.originalB)],['Original whole-wall span',formatLength(s.originalMetres,doc,3)]);
  if(Number.isFinite(s.currentMetres))rows.push(['Current combined piece length',formatLength(s.currentMetres,doc,3)]);
  if(s.currentA)rows.push(['Current pair A',coords(s.currentA)]);
  if(s.currentB)rows.push(['Current pair B',coords(s.currentB)]);
  if(item.originalScan)rows.push(['Original whole-wall scan source','RoomPlan — not an accuracy check'],['Original whole-wall scan time',item.originalScan.capturedAt]);
  const reading=item.originalReading;
  if(reading){
    if(reading.manualInput)rows.push(['Original whole-wall entered reading',reading.manualInput.enteredText+(reading.manualInput.decimalSeparator===','?' · decimal comma':'')]);
    if(reading.appliedLengthInput)rows.push(['Original whole-wall applied entry',reading.appliedLengthInput.enteredText+(reading.appliedLengthInput.decimalSeparator===','?' · decimal comma':'')]);
    if(reading.source==='bluetooth')rows.push(['Original whole-wall tool',reading.deviceModel],['Original whole-wall reading identity',reading.readingID],['Original whole-wall tool reference',reading.toolReference],['Original whole-wall packet',reading.packetHex]);
  }
  return rows;
}
